import { useEffect } from "react"
import { IconSettings } from "@tabler/icons-react"
import { useFeatureFlags } from "../../context/FeatureFlagsProvider"
import { FEATURE_FLAG_META, type FeatureFlagKey } from "../../lib/featureFlags"

type Props = {
  open: boolean
  onClose: () => void
}

const statusLabel = (enabled: boolean) => (enabled ? "On" : "Off")

const AdminFeatureFlagsModal = ({ open, onClose }: Props) => {
  const { flags, buildFlags, overrides, setFlag, resetFlags } = useFeatureFlags()

  useEffect(() => {
    if (!open) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [open, onClose])

  if (!open) return null

  const hasOverrides = Object.keys(overrides).length > 0

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-h-[85vh] max-w-lg overflow-y-auto">
        <div className="mb-5 flex items-start gap-3">
          <span className="rounded-xl bg-[#00205c]/10 p-2 text-[#00205c]">
            <IconSettings size={20} stroke={1.75} />
          </span>
          <div>
            <h3 className="font-outfit text-xl font-semibold text-[#1f1f1f]">Feature Flags</h3>
            <p className="mt-1 font-dm-sans text-sm text-[#787878]">
              Show or hide features for this browser.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {(Object.keys(FEATURE_FLAG_META) as FeatureFlagKey[]).map((key) => {
            const meta = FEATURE_FLAG_META[key]
            const isOverridden = overrides[key] !== undefined

            return (
              <div
                key={key}
                className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-dm-sans text-sm font-semibold text-[#1f1f1f]">{meta.label}</p>
                    <p className="mt-1 font-dm-sans text-xs leading-5 text-[#787878]">
                      {meta.description}
                    </p>
                    <p className="mt-2 font-dm-sans text-[11px] uppercase tracking-wide text-[#94a3b8]">
                      Default: {statusLabel(buildFlags[key])}
                    </p>
                    {isOverridden && (
                      <p className="mt-1 font-dm-sans text-[11px] font-medium uppercase tracking-wide text-[#9e7e43]">
                        Override active
                      </p>
                    )}
                  </div>

                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={flags[key]}
                    onChange={(event) => setFlag(key, event.target.checked)}
                    aria-label={`Toggle ${meta.label}`}
                  />
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-5 rounded-xl border border-dashed border-[#cbd5e1] bg-white px-4 py-3">
          <p className="font-dm-sans text-xs leading-5 text-[#64748b]">
            Desktop: press{" "}
            <span className="font-semibold text-[#00205c]">Cmd/Ctrl + Shift + ,</span> or type{" "}
            <span className="font-semibold text-[#00205c]">dcflags</span>. Mobile: tap five times on{" "}
            <span className="font-semibold text-[#00205c]">Content Manager</span> or{" "}
            <span className="font-semibold text-[#00205c]">Admin CMS</span>.
          </p>
        </div>

        <div className="modal-action mt-6">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={!hasOverrides}
            onClick={resetFlags}
          >
            Reset overrides
          </button>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>

      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onClose}>
          close
        </button>
      </form>
    </dialog>
  )
}

export default AdminFeatureFlagsModal
